import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, MapPin } from 'lucide-react';
import { getStateAbbreviation } from '../../constants/locations';
import './Modals.css';
import './YearSelectionModal.css';

const YearSelectionModal = ({ 
  isOpen, 
  onClose, 
  locationInfo,
  selectedDateRange,
  availableYears,
  onYearChange 
}) => {
  const [focusedButton, setFocusedButton] = useState(null); 

  const getMostRecentYear = useCallback(() => {
    if (!availableYears || availableYears.length === 0) return selectedDateRange;
    return Math.max(...availableYears.map(year => parseInt(year))).toString();
  }, [availableYears, selectedDateRange]);

  const isDataAvailableForSelectedYear = useCallback(() => {
    return availableYears && availableYears.includes(selectedDateRange);
  }, [availableYears, selectedDateRange]);

  useEffect(() => {
    if (!isOpen) {
      setFocusedButton(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
        setTimeout(() => document.activeElement?.blur(), 0);
      } else if (e.key === 'Enter') {
        if (focusedButton === 'current') {
          onYearChange(selectedDateRange);
        } else {
          const mostRecentYear = getMostRecentYear();
          onYearChange(mostRecentYear);
          onClose();
        }
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (isDataAvailableForSelectedYear()) {
          setFocusedButton('current');
        }
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setFocusedButton('recent');
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, getMostRecentYear, onYearChange, isOpen, selectedDateRange, focusedButton, isDataAvailableForSelectedYear]);

  if (!isOpen) return null;

  const formatLocationName = (locationInfo) => {
    if (!locationInfo?.name) return 'Unknown Location';
    
    if ((locationInfo.type === 'county' || locationInfo.type === 'city') && locationInfo.name.includes(',')) {
      const parts = locationInfo.name.split(',');
      const location = parts[0].trim();
      const state = parts[1].trim();
      const stateAbbr = getStateAbbreviation(state);
      return `${location}, ${stateAbbr}`;
    }
    
    if (locationInfo.state) {
      const stateAbbr = getStateAbbreviation(locationInfo.state);
      return `${locationInfo.name}, ${stateAbbr}`;
    }
    
    return locationInfo.name;
  };

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="year-selection-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>More Recent Year Available</h2>
          <button className="close-button" onClick={onClose}>
            <X size={24} />
          </button>
        </div>
        
        <div className="modal-content">
          <div className="search-info year">
            <MapPin size={16} />
            <span>Selected Location: <strong>{formatLocationName(locationInfo)}</strong></span>
          </div>

        </div>

        <div className="modal-actions year">
          {isDataAvailableForSelectedYear() && (
            <button 
              className={`cancel-button ${focusedButton === 'current' ? 'focused' : ''}`}
              onClick={() => {
                onYearChange(selectedDateRange);
              }}
            >
              Keep Selected Year ({selectedDateRange})
            </button>
          )}
          <button 
            className={`confirm-button ${focusedButton === 'recent' ? 'focused' : ''}`}
            onClick={() => {
              const mostRecentYear = getMostRecentYear();
              onYearChange(mostRecentYear);
              onClose();
            }}
          >
            Most Recent Year ({getMostRecentYear()})
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default YearSelectionModal;